/**
 * RightBanner — promotional aside shown alongside the main content.
 * Authenticated users keep the promotional content but do not see guest actions.
 * Matches the Figma desktop right-banner card.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../ui/Button';
import { BrandLogo } from '../ui/BrandLogo';
import { GroupsTabIcon, HeartIcon } from '../ui/icons';
import robiIcon from '../../assets/icons/robi-mobile.png';
import bannerHero from '../../assets/images/robi-phone-hero.webp';
import bannerMobile from '../../assets/images/robi-banner-mobile.webp';
import bannerRobi from '../../assets/images/robi-banner-sliding.webp';
import './layout.css';

const BENEFITS = [
  {
    key: 'realPeople',
    icon: <GroupsTabIcon size={24} aria-hidden />,
  },
  {
    key: 'aiSupport',
    icon: <img src={robiIcon} alt="" aria-hidden />,
  },
  {
    key: 'oneCommunity',
    icon: <HeartIcon size={26} aria-hidden />,
  },
] as const;

export function RightBanner() {
  const { isAuthenticated, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Sign-up issues a token but leaves `isAuthenticated` false until
     `completeAuthSession` runs at the end of onboarding, so a member part-way
     through the flow still counts as a guest by that flag alone. Keying off the
     token too stops the onboarding screens offering "Join for free"/"Sign In"
     to someone who has already registered. */
  const isGuest = !isAuthenticated && !token;

  return (
    <aside className="right-banner">
      <div className="right-banner__card">
        <div className="right-banner__copy">
          <BrandLogo compact className="right-banner__logo" />
          <h2 className="right-banner__title">
            <span>
              {t('home.rightBanner.titleLead')}{' '}
              <span className="right-banner__title-accent">
                {t('home.rightBanner.titleAccent')}
              </span>
            </span>
            <span>{t('home.rightBanner.titleLineTwo')}</span>
          </h2>
          <p className="right-banner__subtitle">{t('home.rightBanner.subtitle')}</p>
          {isGuest && (
            <div className="right-banner__actions">
              <Button size="lg" onClick={() => navigate('/signup')}>
                {t('home.rightBanner.joinForFree')}
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/login')}>
                {t('home.rightBanner.signIn')}
              </Button>
            </div>
          )}
        </div>
        <div className="right-banner__visual" aria-hidden>
          <img
            className="right-banner__image right-banner__image--light"
            src={bannerHero}
            alt=""
          />
          <svg
            className="right-banner__image right-banner__dark-artwork"
            viewBox="69 27 798 761"
            preserveAspectRatio="xMidYMax meet"
            focusable="false"
          >
            <image
              className="right-banner__dark-image right-banner__dark-image--mobile"
              href={bannerMobile}
              x="493"
              y="27"
              width="374"
              height="761"
            />
            <image
              className="right-banner__dark-image right-banner__dark-image--robi"
              href={bannerRobi}
              x="0"
              y="0"
              width="776"
              height="788"
            />
          </svg>
        </div>
        <ul className="right-banner__benefits" aria-label={t('home.rightBanner.benefitsLabel')}>
          {BENEFITS.map(benefit => (
            <li className="right-banner__benefit" key={benefit.key}>
              <span className={`right-banner__benefit-icon right-banner__benefit-icon--${benefit.key}`}>
                {benefit.icon}
              </span>
              <span>{t(`home.rightBanner.${benefit.key}`)}</span>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
